import React, { useState, useEffect } from 'react';
import './ActualiteDetailPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import actualiteService from '../services/actualiteService';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import { toSentenceCase } from '../utils/textUtils';
import useSEO from '../hooks/useSEO';

const IconArrowLeft = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const IconCalendar = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const IconYouTube = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const IconPlay = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const IconExternalLink = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

function ActualiteDetailPage({ slug }) {
  const [actualite, setActualite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useSEO({
    title: actualite?.titre ? `${actualite.titre} | Actualités` : "Détail de l'actualité",
    description: actualite?.resume || (actualite?.contenu ? actualite.contenu.substring(0, 160) : "Consultez cette actualité du Groupe Scolaire et Universitaire Les Palmiers."),
    keywords: "actualité école Les Palmiers, article GSU Les Palmiers, éducation Bénin, événement scolaire Togba",
    canonicalUrl: typeof window !== 'undefined' ? window.location.href : 'https://gsulespalmiers.bj/actualites',
    ogImage: actualite?.image ? getImageUrl(actualite.image) : 'https://gsulespalmiers.bj/images/logo-gsu.png',
    ogType: 'article'
  });

  useEffect(() => {
    const fetchActualite = async () => {
      try {
        setLoading(true);
        const response = await actualiteService.getBySlug(slug);
        setActualite(response.data);
      } catch (err) {
        console.error('Erreur:', err);
        setError('Actualité non trouvée');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchActualite();
    }
  }, [slug]);

  const handleBack = () => {
    if (window.navigateTo) {
      window.navigateTo('actualites');
    }
  };

  // Extraire l'ID YouTube si l'image ou le contenu est lié à une vidéo
  const extractYouTubeId = (urlOrText) => {
    if (!urlOrText) return null;
    const match = urlOrText.match(/\/vi\/([a-zA-Z0-9_-]{11})\//) || 
                  urlOrText.match(/(?:watch\?v=|embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="actualite-detail-page">
        <Header activePage="actualites" />
        <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <div className="spinner-loader"></div>
          <p>Chargement de l'article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !actualite) {
    return (
      <div className="actualite-detail-page">
        <Header activePage="actualites" />
        <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h2>Actualité non trouvée</h2>
          <button onClick={handleBack} className="btn-back">
            <IconArrowLeft size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
            <span>Revenir aux actualités</span>
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const youtubeVideoId = extractYouTubeId(actualite.image) || extractYouTubeId(actualite.contenu);
  const isVideoArticle = Boolean(youtubeVideoId) || actualite.categorie === 'Vidéos & Reportages' || actualite.titre?.toLowerCase().includes('vidéo');

  return (
    <div className="actualite-detail-page">
      <Header activePage="actualites" />
      
      {/* Barre de navigation retour rapide */}
      <div className="detail-top-nav-bar">
        <div className="container">
          <button onClick={handleBack} className="btn-back-prominent">
            <IconArrowLeft size={16} />
            <span>Retour à la liste des actualités</span>
          </button>
          <div className="detail-breadcrumb">
            <span>Accueil</span>
            <span>/</span>
            <span onClick={handleBack} className="breadcrumb-link">Actualités</span>
            <span>/</span>
            <span className="breadcrumb-current">{actualite.titre?.substring(0, 30)}...</span>
          </div>
        </div>
      </div>

      <div className="actualite-detail-hero">
        <div className="container">
          <div className="actualite-meta">
            <span className="meta-date">
              <IconCalendar size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
              {new Date(actualite.date_publication).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
            <span className="meta-category">{toSentenceCase(actualite.categorie)}</span>
            {isVideoArticle && (
              <span className="meta-yt-badge">
                <IconYouTube size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                Vidéo YouTube Officielle
              </span>
            )}
          </div>
          <h1>{toSentenceCase(actualite.titre)}</h1>
        </div>
      </div>

      <div className="actualite-detail-content">
        <div className="container">
          
          {/* LECTEUR VIDÉO YOUTUBE INTÉGRÉ SI C'EST UN REPORTAGE VIDÉO */}
          {youtubeVideoId ? (
            <div className="actualite-video-player-container">
              <div className="video-responsive-wrap">
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&enablejsapi=1`}
                  title={actualite.titre}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="article-youtube-iframe"
                ></iframe>
              </div>
              <div className="video-player-bottom-bar">
                <span className="video-player-hint">
                  <IconPlay size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                  Vidéo officielle diffusée sur la chaîne @cpulespalmiers4431
                </span>
                <a 
                  href={`https://www.youtube.com/watch?v=${youtubeVideoId}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-open-yt-video"
                >
                  <span>Ouvrir sur YouTube</span>
                  <IconExternalLink size={14} />
                </a>
              </div>
            </div>
          ) : actualite.image && (
            <div className="actualite-image-main">
              <img 
                src={getImageUrl(actualite.image, '/images/logo.svg')} 
                alt={actualite.titre}
                onError={(e) => handleImageError(e, '/images/logo.svg')}
              />
            </div>
          )}

          <div className="actualite-body">
            <div className="actualite-description">
              <p className="lead">{toSentenceCase(actualite.description)}</p>
            </div>

            <div className="actualite-contenu" dangerouslySetInnerHTML={{ __html: actualite.contenu }} />

            {/* Encadré Chaîne YouTube Officielle */}
            <div className="actualite-yt-callout">
              <div className="yt-callout-icon">
                <IconYouTube size={28} />
              </div>
              <div className="yt-callout-info">
                <h4>Retrouvez tous nos reportages sur YouTube</h4>
                <p>Abonnez-vous à notre chaîne officielle <strong>@cpulespalmiers4431</strong> pour suivre les démonstrations techniques, les stages et les moments forts de l'établissement.</p>
              </div>
              <a 
                href="https://youtube.com/@cpulespalmiers4431?si=klEjrjQXcjvkGADV" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-yt-callout"
              >
                <span>Voir sur YouTube</span>
                <IconExternalLink size={14} />
              </a>
            </div>
          </div>

          {actualite.images && actualite.images.length > 0 && (
            <div className="actualite-gallery">
              <h3>Galerie Photos</h3>
              <div className="gallery-grid">
                {actualite.images.map((image, index) => (
                  <div key={index} className="gallery-item">
                    <img src={image} alt={`${actualite.titre} - ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ActualiteDetailPage;
